import { eq, unique } from "../utils";

/**
 * Calls `Object.assign(_this, deriveFrom)` and:
 * 
 * - deletes getters from `deriveFrom`, which would otherwise result in `annot set property isReserved of ... which has only a getter`
 */
export function safeObjectAssign(_this: any, deriveFrom: any) {
  if (!deriveFrom) {
    return;
  }

  const safeDeriveFrom: any = {};
  for (const property in deriveFrom) {

    if (Object.getOwnPropertyDescriptor(_this, property)?.writable) {
      safeDeriveFrom[property] = deriveFrom[property];
    }
  }

  return Object.assign(_this, safeDeriveFrom);
}

export function isPojo(object: any) {
  return object && typeof object === 'object' && !Array.isArray(object);
}

/**
 * Returns shallow top-level diff between two objects.
 */
export function getUpdate<T = any>(initial: T, updated: T): Partial<T> | null {
  const diff: any = {};

  if (!isPojo(initial)) {
    return updated; //  Initial object might be null if creating a new one.
  }

  if (!isPojo(updated)) {
    return null;
  }

  for (const key in updated) {
    if (!eq(initial[key], updated[key])) {
      diff[key] = updated[key];
    }
  }

  return diff;
}

/**
 * 
 * @param object Object to extract values from
 * @param getValue Function which receives each nested object and should return a value or undefined
 * @returns A list of values extracted from `object`
 */
export function extractNestedObjectValues<T>(object: any, getValue: (object: any) => T | undefined): T[] {
  if (Array.isArray(object)) {
    return object.map(element => extractNestedObjectValues(element, getValue)).flat();
  }

  const value = getValue(object);
  if (value !== undefined) {
    return [value];
  }

  let values: T[] = [];
  for (const key in object) {
    extractNestedObjectValues(object[key], getValue).forEach(value => values.push(value));
  }

  return values;
}

function ifString(value: any) {
  return typeof value === 'string' ? value : null;
}

/**
 * Summarizes an object's strings.
 * @param object Typically an error object.
 */
export function getObjectStringsSummary(object: any): string {
  const lines: string[] = [];

  const localizedError = ifString(object.localizedMessage) ||
    ifString(object.localizedError) ||
    ifString(object.extra?.localizedMessage) ||
    ifString(object.extra?.localizedError) ||
    ifString(object.extra?.cause?.localizedMessage) ||
    ifString(object.extra?.cause?.localizedError);

  if (localizedError) {
    lines.push(localizedError);

  } else {
    const relevantError = object.extra?.cause ??
      object.extra ?? object;

    const strings = extractNestedObjectValues(
      relevantError,
      value => typeof value === 'string' && value.trim() ? value.trim() : undefined);

    //  Filter out "error", "internalError", "InvalidParameterValue" etc.
    const likelyReadableMessages = strings.filter(string => string.includes(' '));

    if (likelyReadableMessages.length) {
      likelyReadableMessages.forEach(string => lines.push(string));
    }
  }

  return unique(lines).join('\n\n');
}

export function isObject(object: any) {
  return object && typeof object === 'object' && !Array.isArray(object);
}

/**
 * Removes properties not in `keep`.
 */
export function pick<T extends object>(object: T, ...keep: (keyof T)[]): Partial<T> {
  if (!isObject(object)) {
    return object;
  }

  object = { ...object };

  Object.keys(object as any).forEach(key => {
    if (!keep.includes(key as keyof T)) {
      delete object[key];
    }
  });

  return object;
}